import { createContext, useContext, useSyncExternalStore } from 'react';

/**
 * The app's routing: a handful of routes need no router library. The path is
 * read from `location`, `navigate` pushes history and tells subscribers, and
 * the back button works through `popstate`. The cloud answers every app path
 * with index.html (cloud/src/http/statics.ts).
 *
 * Under PlaceProvider (place.tsx, round the app) a move is a React
 * transition: the page moved from stays on screen until the next one is
 * ready, and the window scrolls to the top when it shows.
 */
const listeners = new Set<() => void>();

/** A navigate not yet shown: the window scrolls to the top when its page shows (takeScroll). */
let scrollOwed = false;

export function navigate(to: string, options: { readonly replace?: boolean } = {}): void {
  if (options.replace === true) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  scrollOwed = true;
  for (const listener of listeners) listener();
}

/** Scrolls to the top if a navigate is owed it: PlaceProvider calls it as the moved-to page shows. */
export function takeScroll(): void {
  if (!scrollOwed) return;
  scrollOwed = false;
  window.scrollTo(0, 0);
}

/** To sign-in, coming back here after: a cashier station signed out at the end of a shift opens on the counter again. */
export function toSignIn(): void {
  if (location.pathname === '/sign-in') return;
  navigate(`/sign-in?${new URLSearchParams({ next: location.pathname + location.search }).toString()}`, { replace: true });
}

/** Where sign-in goes on: the page that sent the browser there, a path of this app only. */
export function afterSignIn(): string | null {
  const next = new URLSearchParams(location.search).get('next');
  return next !== null && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

export interface Place {
  readonly path: string;
  readonly query: URLSearchParams;
}

let place: { readonly at: string; readonly place: Place } | undefined;

/** The place as of the last read, the same object until the address changes, so React sees no change where there is none. */
export function currentPlace(): Place {
  const at = location.pathname + location.search;
  if (place?.at !== at) place = { at, place: { path: location.pathname, query: new URLSearchParams(location.search) } };
  return place.place;
}

export function subscribe(update: () => void): () => void {
  listeners.add(update);
  window.addEventListener('popstate', update);
  return () => {
    listeners.delete(update);
    window.removeEventListener('popstate', update);
  };
}

/** The place PlaceProvider shows, and whether a move to another is on its way. */
export interface Shown {
  readonly place: Place;
  readonly moving: boolean;
}

export const PlaceContext = createContext<Shown | null>(null);

const still = () => () => undefined;

/**
 * The page's place: the one PlaceProvider shows, so a page held on screen
 * through a move keeps reading its own. Outside one, read through
 * useSyncExternalStore, which reads it again once it has subscribed: a
 * child that navigates in its first effect (a redirect) runs before its
 * parent subscribes, and is not missed.
 */
export function usePlace(): Place {
  const shown = useContext(PlaceContext);
  const live = useSyncExternalStore(shown === null ? subscribe : still, currentPlace);
  return shown?.place ?? live;
}

/** The place the address names now, even while the page moved from is still on screen: what the menu marks as current, so a tap shows at once. */
export function useAddress(): Place {
  return useSyncExternalStore(subscribe, currentPlace);
}

/** Whether a move to another place is on its way: the page moved from is still on screen. */
export function useMoving(): boolean {
  return useContext(PlaceContext)?.moving ?? false;
}
